import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { apiClient } from '@/utils/api';
import { useAuthStore } from '@/store/authStore';
import { Database, Download, Trash2, Upload, Shield, FileText, Archive, RotateCcw } from 'lucide-react';

interface BackupInfo {
  id: string;
  filename: string;
  type: 'FULL' | 'SCHEMA';
  schema?: string;
  kind?: 'full' | 'schema' | 'snapshot';
  downloadable?: boolean;
  size: number;
  createdAt: string;
  checksum: string;
  isCompressed: boolean;
  isEncrypted: boolean;
}

interface TableRestoreReport {
  model: string;
  dumped: number;
  deleted: number;
  inserted: number;
  failed: number;
  errors: string[];
}

interface RestoreReport {
  kind: 'full' | 'snapshot';
  snapshotFilename?: string;
  tables: TableRestoreReport[];
  failedTotal: number;
  requireRelogin: boolean;
}

interface BackupFormData {
  type: 'FULL' | 'SCHEMA';
  schema: string;
  compress: boolean;
  encrypt: boolean;
  password: string;
}

function backupKind(backup: BackupInfo): 'full' | 'schema' | 'snapshot' {
  return backup.kind || (backup.type === 'SCHEMA' ? 'schema' : 'full');
}

function canRestoreBackup(backup: BackupInfo): boolean {
  const kind = backupKind(backup);
  return kind === 'full' || kind === 'snapshot';
}

export default function BackupPage() {
  const { user, logout } = useAuthStore();
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [schemas, setSchemas] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedBackup, setSelectedBackup] = useState<string>('');
  const [restorePassword, setRestorePassword] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [restoreReport, setRestoreReport] = useState<RestoreReport | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [backupForm, setBackupForm] = useState<BackupFormData>({
    type: 'FULL',
    schema: '',
    compress: true,
    encrypt: false,
    password: ''
  });

  const isAdmin = user?.role === 'ADMIN';
  const restorableBackups = backups.filter(canRestoreBackup);

  useEffect(() => {
    loadBackups();
    loadSchemas();
  }, []);

  const loadBackups = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/backup/list');
      const list = Array.isArray(response.data.data) ? response.data.data : [];
      setBackups(list);
    } catch (error: any) {
      console.error('Error loading backups:', error);
      toast.error('Errore nel caricamento dei backup');
      setBackups([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSchemas = async () => {
    try {
      const response = await apiClient.get('/backup/schemas');
      const list = Array.isArray(response.data.data) ? response.data.data : [];
      setSchemas(list);
    } catch (error: any) {
      console.error('Error loading schemas:', error);
      toast.error('Errore nel caricamento degli schemi');
      setSchemas([]);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setIsCreatingBackup(true);
      setProgress(0);

      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const payload: any = { ...backupForm };
      if (payload.type === 'FULL' || !payload.schema) {
        delete payload.schema;
      }
      if (!payload.encrypt) {
        delete payload.password;
      }

      await apiClient.post('/backup/create', payload);

      clearInterval(progressInterval);
      setProgress(100);

      toast.success(
        backupForm.type === 'SCHEMA'
          ? 'Export schema creato (solo download, non ripristinabile)'
          : 'Backup creato con successo'
      );
      await loadBackups();

      setBackupForm({
        type: 'FULL',
        schema: '',
        compress: true,
        encrypt: false,
        password: ''
      });

    } catch (error: any) {
      console.error('Error creating backup:', error);
      toast.error(error.response?.data?.message || 'Errore nella creazione del backup');
    } finally {
      setIsCreatingBackup(false);
      setProgress(0);
    }
  };

  const handleDownloadBackup = async (backupId: string, filename: string) => {
    try {
      const response = await apiClient.get(`/backup/download/${backupId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Download avviato con successo!');
    } catch (error: any) {
      console.error('Error downloading backup:', error);
      const msg = error.response?.data?.message || 'Errore durante il download del backup';
      toast.error(msg);
    }
  };

  const handleDeleteBackup = async (backupId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo backup?')) return;

    try {
      await apiClient.delete(`/backup/delete/${backupId}`);
      toast.success('Backup eliminato con successo');
      await loadBackups();
    } catch (error: any) {
      console.error('Error deleting backup:', error);
      toast.error('Errore durante l\'eliminazione del backup');
    }
  };

  const handleRestoreBackup = async (explicitId?: string) => {
    const backupIdToUse = explicitId || selectedBackup;
    if (!backupIdToUse && !uploadedFile) {
      toast.error('Seleziona un backup dalla lista oppure carica un file');
      return;
    }

    const chosen = backups.find((b) => b.id === backupIdToUse);
    if (chosen && !canRestoreBackup(chosen) && !uploadedFile) {
      toast.error('Il backup per schema è solo esportabile. Usa un backup FULL.');
      return;
    }

    const sourceLabel = uploadedFile?.name || chosen?.filename || backupIdToUse;
    const isSnapshot = chosen ? backupKind(chosen) === 'snapshot' : false;

    if (!window.confirm(
      isSnapshot
        ? `Rollback allo snapshot "${sourceLabel}"? Verranno ripristinati anche email/WhatsApp/token di quella macchina.`
        : `ATTENZIONE: il ripristino di "${sourceLabel}" sovrascriverà i dati attuali. Email e WhatsApp verranno svuotati. Verrà creato uno snapshot di sicurezza (48h). Continuare?`
    )) return;

    try {
      setIsRestoring(true);
      setRestoreReport(null);

      let backupId = backupIdToUse;
      if (uploadedFile && !explicitId) {
        const formData = new FormData();
        formData.append('file', uploadedFile);
        const uploadResponse = await apiClient.post('/backup/upload', formData, {
          timeout: 10 * 60 * 1000,
        });
        backupId = uploadResponse.data?.data?.id;
        if (!backupId) {
          throw new Error('Upload riuscito ma manca l\'identificativo del backup');
        }
        await loadBackups();
      }

      const payload: { backupId: string; password?: string } = { backupId };
      if (restorePassword) {
        payload.password = restorePassword;
      }

      const response = await apiClient.post('/backup/restore', payload, {
        timeout: 15 * 60 * 1000,
        validateStatus: (status) => status < 500,
      });

      if (response.status === 400 || response.status === 403) {
        toast.error(response.data?.message || 'Ripristino rifiutato');
        return;
      }

      const report = response.data?.data as RestoreReport | undefined;
      if (report?.tables) {
        setRestoreReport(report);
      }

      const failed = report?.failedTotal ?? (response.data?.success ? 0 : 1);
      if (failed === 0 && response.data?.success) {
        toast.success(response.data.message || 'Backup ripristinato con successo');
      } else {
        toast.error(response.data?.message || 'Ripristino con errori. Vedi il report.');
      }

      if (report?.tables) {
        const lines = report.tables
          .filter((t) => t.dumped > 0 || t.deleted > 0 || t.failed > 0)
          .map((t) => `${t.model}: dump ${t.dumped} · del ${t.deleted} · ins ${t.inserted}${t.failed ? ` · fail ${t.failed}` : ''}`);
        window.alert(
          `${response.data?.message || 'Ripristino completato'}\n` +
            (report.snapshotFilename ? `Snapshot: ${report.snapshotFilename}\n` : '') +
            `\n${lines.slice(0, 40).join('\n')}` +
            (lines.length > 40 ? `\n… +${lines.length - 40} tabelle` : '')
        );
      }

      setSelectedBackup('');
      setRestorePassword('');
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (report?.requireRelogin !== false) {
        toast.message('Devi accedere di nuovo dopo il ripristino.');
        await logout();
        return;
      }

      await loadBackups();
    } catch (error: any) {
      console.error('Error restoring backup:', error);
      toast.error(error.response?.data?.message || error.message || 'Errore durante il ripristino del backup');
    } finally {
      setIsRestoring(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setUploadedFile(null);
      return;
    }
    setUploadedFile(file);
    setSelectedBackup('');
    toast.success(`File selezionato: ${file.name}`);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getBackupIcon = (backup: BackupInfo) => {
    if (backupKind(backup) === 'snapshot') return <RotateCcw className="h-4 w-4 text-amber-600" />;
    if (backup.isEncrypted) return <Shield className="h-4 w-4 text-red-500" />;
    if (backup.isCompressed) return <Archive className="h-4 w-4 text-blue-500" />;
    return <FileText className="h-4 w-4 text-gray-500" />;
  };

  const kindLabel = (backup: BackupInfo) => {
    const kind = backupKind(backup);
    if (kind === 'snapshot') return 'Snapshot pre-restore (48h, non scaricabile)';
    if (kind === 'schema') return `Export schema: ${backup.schema} (solo download)`;
    return 'Backup completo';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Database className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Backup & Restore</h1>
          <p className="text-gray-600">Gestisci i backup del database</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Database className="h-5 w-5" />
            <span>Crea Backup</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="backupType">Tipo di Backup</Label>
              <Select
                value={backupForm.type}
                onValueChange={(value: 'FULL' | 'SCHEMA') => setBackupForm({...backupForm, type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona tipo di backup" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FULL">Backup Completo Database</SelectItem>
                  <SelectItem value="SCHEMA">Export singola tabella (solo download)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {backupForm.type === 'SCHEMA' && (
              <div>
                <Label htmlFor="selectedSchema">Seleziona Schema</Label>
                <Select
                  value={backupForm.schema}
                  onValueChange={(value) => setBackupForm({...backupForm, schema: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona uno schema" />
                  </SelectTrigger>
                  <SelectContent>
                    {schemas.map((schema) => (
                      <SelectItem key={schema} value={schema}>{schema}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {backupForm.type === 'SCHEMA' && (
            <p className="text-sm text-amber-700">
              L&apos;export per tabella non è ripristinabile. Per clonare o fare DR usa un backup completo.
            </p>
          )}

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="compressBackup"
                checked={backupForm.compress}
                onCheckedChange={(checked: boolean) => setBackupForm({...backupForm, compress: checked})}
              />
              <Label htmlFor="compressBackup">Comprimi Backup (.aibackup)</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="encryptBackup"
                checked={backupForm.encrypt}
                onCheckedChange={(checked: boolean) => setBackupForm({...backupForm, encrypt: checked})}
              />
              <Label htmlFor="encryptBackup">Cripta Backup</Label>
            </div>

            {backupForm.encrypt && (
              <div>
                <Label htmlFor="backupPassword">Password per Criptazione</Label>
                <Input
                  id="backupPassword"
                  type="password"
                  value={backupForm.password}
                  onChange={(e) => setBackupForm({...backupForm, password: e.target.value})}
                  placeholder="Inserisci password per criptare il backup"
                />
              </div>
            )}
          </div>

          {isCreatingBackup && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Creazione backup in corso...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="w-full" />
            </div>
          )}

          <Button
            onClick={handleCreateBackup}
            disabled={isCreatingBackup || (backupForm.type === 'SCHEMA' && !backupForm.schema)}
            className="w-full"
          >
            {isCreatingBackup ? 'Creazione in corso...' : 'Crea Backup'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="h-5 w-5" />
            <span>Ripristina Backup</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="restoreBackup">Seleziona Backup da Ripristinare</Label>
            <Select
              value={selectedBackup || undefined}
              onValueChange={(value) => {
                setSelectedBackup(value);
                setUploadedFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleziona backup FULL o snapshot" />
              </SelectTrigger>
              <SelectContent>
                {restorableBackups.map((backup) => (
                  <SelectItem key={backup.id} value={backup.id}>
                    <div className="flex items-center space-x-2">
                      {getBackupIcon(backup)}
                      <span>{backup.filename}</span>
                      <span className="text-sm text-gray-500">
                        ({formatFileSize(backup.size)})
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {((selectedBackup && backups.find(b => b.id === selectedBackup)?.isEncrypted) ||
            (uploadedFile && /\.enc\./i.test(uploadedFile.name))) && (
            <div>
              <Label htmlFor="restorePassword">Password per Backup Criptato</Label>
              <Input
                id="restorePassword"
                type="password"
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                placeholder="Inserisci password del backup"
              />
            </div>
          )}

          <div>
            <Label htmlFor="fileUpload">Oppure carica file FULL (.json / .aibackup)</Label>
            <Input
              id="fileUpload"
              ref={fileInputRef}
              type="file"
              accept=".json,.aibackup"
              onChange={handleFileUpload}
              className="mt-2"
            />
            {uploadedFile && (
              <p className="mt-2 text-sm text-gray-600">
                File pronto per il ripristino: <span className="font-medium">{uploadedFile.name}</span>
              </p>
            )}
          </div>

          {!isAdmin && (
            <p className="text-sm text-red-600">Solo gli amministratori possono ripristinare i backup.</p>
          )}
          {isAdmin && !selectedBackup && !uploadedFile && (
            <p className="text-sm text-gray-500">
              Seleziona un backup FULL o uno snapshot, oppure carica un file v2, poi premi Ripristina.
            </p>
          )}

          <Button
            onClick={() => handleRestoreBackup()}
            disabled={!isAdmin || isRestoring || (!selectedBackup && !uploadedFile)}
            variant="destructive"
            className="w-full"
          >
            {isRestoring ? 'Ripristino in corso...' : 'Ripristina Backup (Solo Admin)'}
          </Button>
        </CardContent>
      </Card>

      {restoreReport && (
        <Card>
          <CardHeader>
            <CardTitle>Report ripristino</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className={restoreReport.failedTotal > 0 ? 'text-red-600' : 'text-green-700'}>
              {restoreReport.failedTotal > 0
                ? `${restoreReport.failedTotal} errori`
                : 'Nessun errore'}
              {restoreReport.snapshotFilename ? ` • snapshot: ${restoreReport.snapshotFilename}` : ''}
            </p>
            <div className="max-h-64 overflow-auto text-sm">
              {restoreReport.tables.filter((t) => t.dumped > 0 || t.deleted > 0 || t.failed > 0).map((t) => (
                <div key={t.model} className="flex justify-between border-b py-1">
                  <span className="font-medium">{t.model}</span>
                  <span>
                    dump {t.dumped} · del {t.deleted} · ins {t.inserted}
                    {t.failed > 0 ? ` · fail ${t.failed}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Backup Disponibili</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4">Caricamento backup...</div>
          ) : backups.length === 0 ? (
            <div className="text-center py-4 text-gray-500">Nessun backup trovato</div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup) => (
                <div key={backup.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getBackupIcon(backup)}
                    <div>
                      <h4 className="font-medium">{backup.filename}</h4>
                      <div className="text-sm text-gray-500">
                        {kindLabel(backup)} • {formatFileSize(backup.size)} • {new Date(backup.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {backupKind(backup) === 'snapshot' && isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isRestoring}
                        onClick={() => handleRestoreBackup(backup.id)}
                      >
                        Rollback
                      </Button>
                    )}
                    {backup.downloadable !== false && backupKind(backup) !== 'snapshot' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadBackup(backup.id, backup.filename)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteBackup(backup.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
